import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ClipboardList, Users, UserPlus, Search, X, CheckCircle2, Circle, ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import { formatDisplayName } from "@/lib/names";

const STATUS_META = {
  guest:    { label: "Guest",    tint: "var(--muted)" },
  disciple: { label: "Disciple", tint: "var(--orange)" },
  member:   { label: "Member",   tint: "var(--gold)" },
};
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.guest;
  return <span className="text-[0.55rem] uppercase tracking-wider px-1.5 py-0.5 shrink-0" style={{ background: meta.tint, color: "#FFFFFF" }}>{meta.label}</span>;
}

function AddSomeoneModal({ sectionId, onClose, onAdded }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(null);
  const search = async () => {
    setLoading(true);
    try { const r = await api.get("/att/section-leader/people", { params: { q: query } }); setResults(r.data || []); }
    catch (err) { toast.error(err?.response?.data?.detail || "Search failed."); }
    finally { setLoading(false); }
  };
  useEffect(() => { search(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(search, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [query]);
  const addPerson = async (p) => {
    setAdding(p.id);
    try {
      await api.post(`/att/section-leader/sections/${sectionId}/add-person`, { guest_id: p.id });
      toast.success(`${formatDisplayName(p)} added.`); onAdded?.(); onClose?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't add."); }
    finally { setAdding(null); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-10 px-4" style={{ background: "rgba(14,34,64,0.55)" }} onClick={onClose} data-testid="add-someone-overlay">
      <div className="w-full max-w-xl rounded-md" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }} onClick={(e) => e.stopPropagation()}>
        <div className="p-5 flex items-center justify-between gap-3 border-b" style={{ borderColor: "var(--border-subtle)" }}>
          <div><div className="overline" style={{ color: "var(--terracotta)", fontSize: "0.6rem" }}>Add to your section</div><h3 className="font-serif-display text-2xl leading-tight" style={{ color: "var(--ink)" }}>Find someone</h3></div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[var(--surface-hover)]" style={{ color: "var(--muted)" }} data-testid="add-someone-close"><X size={18} /></button>
        </div>
        <div className="p-5">
          <div className="flex items-center gap-2 px-3 py-2 rounded" style={{ background: "var(--surface-hover)" }}>
            <Search size={16} style={{ color: "var(--muted)" }} />
            <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email, or phone…" className="flex-1 bg-transparent text-sm outline-none" style={{ color: "var(--ink)" }} data-testid="add-someone-search" />
          </div>
          <div className="mt-4 space-y-1 max-h-[24rem] overflow-y-auto">
            {loading && <p className="text-sm p-3" style={{ color: "var(--muted)" }}>Searching…</p>}
            {!loading && results.length === 0 && <p className="text-sm p-3" style={{ color: "var(--muted)" }}>No one matches.</p>}
            {results.map((p) => {
              const already = p.section_id === sectionId;
              return (
                <div key={p.id} className="flex items-center justify-between gap-3 p-3 rounded" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap"><span className="font-medium text-sm" style={{ color: "var(--ink)" }}>{formatDisplayName(p)}</span><StatusBadge status={p.lifecycle_status} /></div>
                    {(p.phone || p.email) && <div className="text-[0.7rem] mt-0.5" style={{ color: "var(--muted)" }}>{p.phone || p.email}</div>}
                  </div>
                  {already ? <span className="text-[0.65rem] uppercase tracking-wider" style={{ color: "var(--olive)" }}>Already in section</span>
                           : <button onClick={() => addPerson(p)} disabled={adding === p.id} className="btn-primary text-xs" data-testid={`person-add-${p.id}`}>{adding === p.id ? "Adding…" : "Add"}</button>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// A "today card" — one per scheduled service today. Big tap targets, present/absent toggle.
function TodayServiceCard({ service, roster, events, dateIso, onMark }) {
  const eventBySection = useMemo(() => {
    const map = new Set();
    events.filter((e) => e.service_id === service.id && e.event_date === dateIso && e.present).forEach((e) => map.add(e.guest_id));
    return map;
  }, [events, service.id, dateIso]);
  const [busy, setBusy] = useState(null);
  const toggle = async (guest, present) => {
    setBusy(guest.id);
    try { await onMark(guest.id, service.id, dateIso, present); }
    finally { setBusy(null); }
  };
  const presentCount = roster.filter((g) => eventBySection.has(g.id)).length;
  return (
    <div className="card-surface p-5" data-testid={`today-service-${service.id}`}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="overline" style={{ color: "var(--gold)", fontSize: "0.6rem" }}>Today · {service.start_time}</div>
          <h3 className="font-serif-display text-2xl" style={{ color: "var(--ink)" }}>{service.name}</h3>
        </div>
        <div className="text-xs" style={{ color: "var(--ink-2)" }}><strong style={{ color: "var(--gold)" }}>{presentCount}</strong> / {roster.length} present</div>
      </div>
      {roster.length === 0 ? (
        <p className="text-sm italic px-3 py-2" style={{ color: "var(--muted)", background: "var(--bg-alt)" }}>No one in your section yet. Tap "Add someone" above.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {roster.map((g) => {
            const present = eventBySection.has(g.id);
            return (
              <button
                key={g.id}
                onClick={() => toggle(g, !present)}
                disabled={busy === g.id}
                className="flex items-center justify-between gap-3 p-3 rounded text-left transition-colors"
                style={{ background: present ? "rgba(212,163,90,0.15)" : "var(--bg-alt)", border: `1px solid ${present ? "var(--gold)" : "var(--border-subtle)"}` }}
                data-testid={`mark-${service.id}-${g.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm" style={{ color: "var(--ink)" }}>{formatDisplayName(g)}</div>
                  <div className="text-[0.65rem]" style={{ color: "var(--muted)" }}>{g.lifecycle_status}</div>
                </div>
                {present ? <CheckCircle2 size={22} style={{ color: "var(--gold)" }} /> : <Circle size={20} style={{ color: "var(--muted)" }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Compact weekly grid for a chosen ISO week (Sun-Sat).
function WeekGrid({ services, roster, events, weekStart, onMark }) {
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + i);
    return { iso: d.toISOString().slice(0, 10), label: DAYS[d.getUTCDay()], date: d.getUTCDate() };
  }), [weekStart]);
  // For each service, use its day_of_week
  const scheduled = useMemo(() => services.map((s) => ({ svc: s, day: days[s.day_of_week] })), [services, days]);
  const eventKey = (guestId, svcId, dateIso) => `${guestId}|${svcId}|${dateIso}`;
  const present = useMemo(() => {
    const set = new Set();
    events.forEach((e) => { if (e.present) set.add(eventKey(e.guest_id, e.service_id, e.event_date)); });
    return set;
  }, [events]);
  const [busy, setBusy] = useState(null);
  if (scheduled.length === 0) return <p className="text-sm italic" style={{ color: "var(--muted)" }}>No services scheduled.</p>;
  return (
    <div className="overflow-x-auto" data-testid="week-grid">
      <table className="w-full text-sm" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
        <thead>
          <tr>
            <th className="text-left px-3 py-2 min-w-[10rem]" style={{ background: "var(--bg-alt)", borderBottom: "1px solid var(--border-subtle)" }}>Person</th>
            {scheduled.map(({ svc, day }) => (
              <th key={svc.id} className="px-2 py-2 text-center min-w-[6rem]" style={{ background: "var(--bg-alt)", borderBottom: "1px solid var(--border-subtle)" }}>
                <div className="text-[0.6rem]" style={{ color: "var(--muted)" }}>{day.label} {day.date}</div>
                <div className="font-medium" style={{ color: "var(--ink)" }}>{svc.name}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {roster.map((g) => (
            <tr key={g.id} data-testid={`week-row-${g.id}`}>
              <td className="px-3 py-2" style={{ background: "var(--bg-alt)", borderBottom: "1px solid var(--border-subtle)" }}>
                <div style={{ color: "var(--ink)" }}>{formatDisplayName(g, { lastFirst: true })}</div>
              </td>
              {scheduled.map(({ svc, day }) => {
                const on = present.has(eventKey(g.id, svc.id, day.iso));
                return (
                  <td key={svc.id} className="text-center p-1" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                    <button
                      onClick={async () => { setBusy(`${g.id}|${svc.id}`); try { await onMark(g.id, svc.id, day.iso, !on); } finally { setBusy(null); } }}
                      className="w-9 h-9 rounded flex items-center justify-center transition-colors"
                      style={{ background: on ? "var(--gold)" : "var(--bg-base)", border: `1px solid ${on ? "var(--gold)" : "var(--border-strong)"}` }}
                      data-testid={`week-cell-${g.id}-${svc.id}-${day.iso}`}
                    >
                      {on ? <CheckCircle2 size={16} style={{ color: "#FFFFFF" }} /> : <Circle size={14} style={{ color: "var(--muted)" }} />}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function isoDate(d) { return new Date(d).toISOString().slice(0, 10); }
function startOfWeek(iso) { const d = new Date(iso + "T00:00:00Z"); d.setUTCDate(d.getUTCDate() - d.getUTCDay()); return d.toISOString().slice(0, 10); }

export default function SectionLeaderHome() {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [events, setEvents] = useState([]);
  const [weekStart, setWeekStart] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [tab, setTab] = useState("today");
  const [allServices, setAllServices] = useState([]);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/att/me");
      setData(r.data);
      const today = r.data.today || isoDate(new Date());
      setWeekStart((prev) => prev || startOfWeek(today));
    } catch { setData(null); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (user) load(); }, [user]);

  useEffect(() => {
    if (!data?.section || !weekStart) return;
    const start = weekStart;
    const end = (() => { const d = new Date(start + "T00:00:00Z"); d.setUTCDate(d.getUTCDate() + 6); return d.toISOString().slice(0, 10); })();
    api.get("/att/section-leader/events", { params: { start, end } })
      .then((r) => setEvents(r.data.events || []))
      .catch(() => setEvents([]));
  }, [data, weekStart]);

  const mark = async (guest_id, service_id, event_date, present) => {
    // optimistic
    setEvents((prev) => present
      ? [...prev.filter((e) => !(e.guest_id === guest_id && e.service_id === service_id && e.event_date === event_date)), { guest_id, service_id, event_date, present: true }]
      : prev.filter((e) => !(e.guest_id === guest_id && e.service_id === service_id && e.event_date === event_date)));
    try {
      await api.post("/att/section-leader/events", { guest_id, service_id, event_date, present });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save.");
      // reload on error to sync
      const start = weekStart;
      const end = (() => { const d = new Date(start + "T00:00:00Z"); d.setUTCDate(d.getUTCDate() + 6); return d.toISOString().slice(0, 10); })();
      const r = await api.get("/att/section-leader/events", { params: { start, end } });
      setEvents(r.data.events || []);
    }
  };

  const shiftWeek = (deltaDays) => {
    const d = new Date(weekStart + "T00:00:00Z"); d.setUTCDate(d.getUTCDate() + deltaDays); setWeekStart(d.toISOString().slice(0, 10));
  };

  useEffect(() => {
    setAllServices(data?.services || []);
  }, [data]);

  if (loading) return null;
  if (!user) return <Navigate to="/attendance/login" replace />;

  const roster = data?.roster || [];
  const servicesToday = data?.services_today || [];
  const today = data?.today || isoDate(new Date());

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-12 py-10" data-testid="section-leader-home">
      <div className="overline mb-1" style={{ color: "var(--terracotta)" }}>Section leader</div>
      <h1 className="font-serif-display text-4xl" style={{ color: "var(--ink)" }}>Your section</h1>

      {busy ? (
        <p className="mt-6 text-sm" style={{ color: "var(--muted)" }}>Loading…</p>
      ) : !data?.section ? (
        <div className="mt-8 p-8 rounded-md text-center" style={{ background: "var(--bg-alt)", border: "1px dashed var(--border-subtle)" }}>
          <ClipboardList size={28} style={{ color: "var(--muted)", margin: "0 auto 0.75rem" }} />
          <h3 className="font-serif-display text-xl" style={{ color: "var(--ink)" }}>No section yet</h3>
          <p className="text-sm mt-2" style={{ color: "var(--muted)" }}>Your coordinator hasn't assigned you to a section yet. Once they do, you'll see your roster and today's services here.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-5">
          <div className="p-5 rounded-md flex items-start justify-between gap-3 flex-wrap" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }}>
            <div>
              <div className="overline mb-1" style={{ fontSize: "0.6rem", color: "var(--muted)" }}>Section</div>
              <h2 className="font-serif-display text-2xl" style={{ color: "var(--ink)" }}>{data.section.name}</h2>
              <div className="mt-2 flex items-center gap-2 text-xs" style={{ color: "var(--ink-2)" }}><Users size={14} /> {roster.length} {roster.length === 1 ? "person" : "people"} in your care</div>
            </div>
            <button onClick={() => setAddOpen(true)} className="btn-primary" data-testid="add-someone-btn"><UserPlus size={14} /> Add someone</button>
          </div>

          <div className="inline-flex rounded-full p-0.5" style={{ background: "var(--surface-hover)" }} data-testid="sl-view-tabs">
            <button onClick={() => setTab("today")} data-testid="sl-tab-today" className="px-4 py-1.5 text-xs rounded-full transition-colors" style={{ background: tab === "today" ? "var(--ink)" : "transparent", color: tab === "today" ? "#FFFFFF" : "var(--ink-2)", fontWeight: tab === "today" ? 600 : 400 }}>Today</button>
            <button onClick={() => setTab("week")} data-testid="sl-tab-week" className="px-4 py-1.5 text-xs rounded-full transition-colors" style={{ background: tab === "week" ? "var(--ink)" : "transparent", color: tab === "week" ? "#FFFFFF" : "var(--ink-2)", fontWeight: tab === "week" ? 600 : 400 }}>This week's grid</button>
          </div>

          {tab === "today" ? (
            servicesToday.length === 0 ? (
              <div className="p-6 rounded-md" style={{ background: "var(--bg-alt)", border: "1px dashed var(--border-subtle)" }} data-testid="no-services-today">
                <div className="flex items-start gap-3">
                  <Calendar size={18} style={{ color: "var(--muted)" }} />
                  <div>
                    <h3 className="font-serif-display text-lg" style={{ color: "var(--ink)" }}>No services scheduled today</h3>
                    <p className="text-sm mt-1" style={{ color: "var(--muted)" }}>Switch to <button onClick={() => setTab("week")} className="underline">this week's grid</button> to see other days, or check back another day.</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {servicesToday.map((s) => (
                  <TodayServiceCard key={s.id} service={s} roster={roster} events={events} dateIso={today} onMark={mark} />
                ))}
              </div>
            )
          ) : (
            <div className="card-surface p-5">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div>
                  <div className="overline" style={{ color: "var(--gold)", fontSize: "0.6rem" }}>Week of</div>
                  <h3 className="font-serif-display text-xl" style={{ color: "var(--ink)" }}>{weekStart}</h3>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => shiftWeek(-7)} className="btn-ghost" data-testid="week-prev"><ChevronLeft size={14} /></button>
                  <button onClick={() => setWeekStart(startOfWeek(today))} className="btn-ghost text-xs" data-testid="week-today">This week</button>
                  <button onClick={() => shiftWeek(7)} className="btn-ghost" data-testid="week-next"><ChevronRight size={14} /></button>
                </div>
              </div>
              <WeekGrid services={allServices} roster={roster} events={events} weekStart={weekStart} onMark={mark} />
            </div>
          )}
        </div>
      )}

      {addOpen && data?.section && <AddSomeoneModal sectionId={data.section.id} onClose={() => setAddOpen(false)} onAdded={load} />}
    </div>
  );
}
